import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, qs } from '../lib/api';
import { humanise } from '../lib/format';
import { Badge, EmptyState, Input, ListRow, Modal } from './ui';

interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle: string | null;
  badge: string | null;
  link: string;
}

const TYPE_LABEL: Record<string, string> = {
  machine: 'Machine',
  component: 'Component',
  problem: 'Problem tag',
  opl: 'OPL',
  checklist: 'Checklist',
  operator: 'Person',
  line: 'Line',
};

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();

  // Debounce so typing a machine code does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 220);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<{ data: SearchHit[] }>(`/api/search${qs({ q: debounced })}`),
    enabled: open && debounced.length >= 2,
  });

  const grouped = (data?.data ?? []).reduce<Record<string, SearchHit[]>>((acc, hit) => {
    (acc[hit.type] ??= []).push(hit);
    return acc;
  }, {});

  return (
    <Modal open={open} onClose={onClose} title="Search">
      <div className="space-y-4">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-steel-400"
          />
          <Input
            autoFocus
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Machine ID, problem tag, OPL number, person…"
            className="pl-9"
            aria-label="Search ODR"
          />
        </div>

        {debounced.length < 2 ? (
          <p className="px-1 text-sm text-steel-500">
            Type at least two characters. You can search by machine ID (FILL-01), problem tag
            (PT-00012), OPL number (AM-CLN-001), checklist, component or person.
          </p>
        ) : isFetching ? (
          <p className="py-6 text-center text-sm text-steel-500">Searching…</p>
        ) : Object.keys(grouped).length === 0 ? (
          <EmptyState
            icon={Search}
            title="No matches"
            message={`Nothing found for "${debounced}". Check the spelling, or try part of the ID.`}
          />
        ) : (
          <div className="-mx-4 divide-y divide-steel-100">
            {Object.entries(grouped).map(([type, hits]) => (
              <div key={type}>
                <p className="bg-steel-50 px-4 py-1.5 text-xs font-bold tracking-wide text-steel-500 uppercase">
                  {TYPE_LABEL[type] ?? humanise(type)}
                </p>
                {hits.map((hit) => (
                  <ListRow
                    key={`${hit.type}-${hit.id}`}
                    onClick={() => {
                      onClose();
                      navigate(hit.link);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-steel-900">{hit.title}</p>
                      {hit.badge ? <Badge>{humanise(hit.badge)}</Badge> : null}
                    </div>
                    {hit.subtitle ? (
                      <p className="truncate text-xs text-steel-500">{hit.subtitle}</p>
                    ) : null}
                  </ListRow>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
